import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGlynt from './useGlynt';
import { clearCachedCertification } from '../../utils/certificationCache';
import { IRQCallbacks } from '../../types/my-types';
import { IGroundTruthUpdateParams } from '../../api/types';

type IGroundTruthCreateMutationParams = {
  trainingSetId: string;
  id: string;
  mutationData: IGroundTruthUpdateParams;
};

export const useGroundTruthUpdate = (optimisticUpdate: boolean, callbacks?: IRQCallbacks) => {
  const glynt = useGlynt();
  const cache = useQueryClient();

  return useMutation({
    mutationFn: async (data: IGroundTruthCreateMutationParams) => {
      return await (await glynt).groundTruths.update(data.id, data.mutationData);
    },

    ...callbacks,

    onSettled: async (data, error, variables) => {
      // Only on success: a failed edit revoked nothing server-side.
      if (!error) {
        await clearCachedCertification(cache, variables.trainingSetId);
      }

      // Invalidate mark validation for this document
      cache.invalidateQueries({
        queryKey: [
          'mark-validation',
          variables.trainingSetId,
          variables.mutationData.document,
        ]
      });

      if (!optimisticUpdate) {
        cache.invalidateQueries({
          queryKey: ['groundTruths']
        });
      }

      // Call the original onSettled if provided
      callbacks?.onSettled?.();
    },

    onMutate: async (data) => {
      if (optimisticUpdate) {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await cache.cancelQueries({
          queryKey: [
            'groundTruths',
            data.mutationData.document,
            data.trainingSetId,
          ]
        });

        // // Snapshot the previous value
        const previousGroundTruths: any = cache.getQueryData([
          'groundTruths',
          data.mutationData.document,
          data.trainingSetId,
        ]);

        // Grab the previous pages and results
        const previousPages = previousGroundTruths.pages || {};

        // Find and update the ground truth that is being edited
        // Results are paginated so we need to filter through all pages
        const newPages = previousPages.map((page: any, index: number) => {
          const newReults = page.results.map((result: any) => {
            if (result.id === data.id) {
              const optimisticUpdateResult = {
                ...result,
                ...data.mutationData,
              };
              return optimisticUpdateResult;
            } else {
              return result;
            }
          });

          // Return a new page object with the updated results
          return {
            ...page,
            results: newReults,
          };
        });

        // Update ground truths object with updated results
        const updatedGroundTruths = {
          ...previousGroundTruths,
          pages: newPages,
        };

        // Update cache with updated ground truths
        cache.setQueryData(
          ['groundTruths', data.mutationData.document, data.trainingSetId],
          updatedGroundTruths
        );

        // Return a context object with the snapshotted value
        return { previousGroundTruths };
      }
    }
  });
};
